import React, { useState, useEffect } from "react";
import { Modal, Upload, Button, message } from "antd";
import { UploadOutlined, LoadingOutlined, SaveOutlined, CloseOutlined, PictureOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import AddImage from "../../Global Functions/addImage";
import { updatePropertyMedia } from "../../Redux/Actions/rentalPropertiesActions";
import "./updatePropertyMediaPopup.css";

const FIELD_CONFIG = [
    { key: "cover_photos", label: "Cover Photos" },
    { key: "images", label: "Property Images" },
    { key: "cover_photos_thumbnail", label: "Cover Photos (Thumbnail)" },
    { key: "images_thumbnail", label: "Property Images (Thumbnail)" },
];

/* Normalizes whatever shape the view API returned (array of strings or
   array of {url, index}) into antd Upload's fileList shape, so existing
   images show up as already-uploaded items in each Upload component. */
const toFileList = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((item, i) => {
        const url = typeof item === "string" ? item : item?.url;
        return {
            uid: `existing-${i}-${url}`,
            name: url?.split("/").pop() || `image-${i}`,
            status: "done",
            url,
        };
    });
};

const fileListToPayload = (fileList) =>
    fileList
        .filter((f) => f.status === "done" && f.url)
        .map((f, i) => ({ url: f.url, index: i }));

const UpdatePropertyMediaPopup = ({ isOpen, onClose, propertyId, existingData }) => {
    const dispatch = useDispatch();

    const [fileLists, setFileLists] = useState({
        cover_photos: [],
        images: [],
        cover_photos_thumbnail: [],
        images_thumbnail: [],
    });
    const [uploading, setUploading] = useState({
        cover_photos: false,
        images: false,
        cover_photos_thumbnail: false,
        images_thumbnail: false,
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFileLists({
                cover_photos: toFileList(existingData?.cover_photos),
                images: toFileList(existingData?.images),
                cover_photos_thumbnail: toFileList(existingData?.cover_photos_thumbnail),
                images_thumbnail: toFileList(existingData?.images_thumbnail),
            });
        }
    }, [isOpen, existingData]);

    if (!isOpen) return null;

    const makeCustomUpload = (key) => async ({ file, onSuccess, onError }) => {
        try {
            setUploading((prev) => ({ ...prev, [key]: true }));
            const sanitizedFile = new File([file], file.name.replace(/\s+/g, "_"), { type: file.type });
            const url = await AddImage(sanitizedFile);
            onSuccess({ url }, file);
            setFileLists((prev) => ({
                ...prev,
                [key]: prev[key].map((f) =>
                    f.uid === file.uid ? { ...f, status: "done", url } : f
                ),
            }));
        } catch (err) {
            message.error(`${FIELD_CONFIG.find((f) => f.key === key)?.label || "Image"} upload failed!`);
            onError(err);
        } finally {
            setUploading((prev) => ({ ...prev, [key]: false }));
        }
    };

    const handleChange = (key) => ({ fileList }) => {
        setFileLists((prev) => ({ ...prev, [key]: fileList }));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const payload = { property_id: propertyId };
            FIELD_CONFIG.forEach(({ key }) => {
                payload[key] = fileListToPayload(fileLists[key]);
            });

            const res = await dispatch(updatePropertyMedia(payload));
            if (res?.statusCode === 200) {
                onClose();
            }
        } catch {
            message.error("Failed to update media");
        } finally {
            setSubmitting(false);
        }
    };

    const anyUploading = Object.values(uploading).some(Boolean);

    return (
        <Modal
            title={
                <div className="upm-modal-header-content">
                    <PictureOutlined style={{ color: "white" }} />
                    <span style={{ color: "white" }}>UPDATE PROPERTY MEDIA</span>
                </div>
            }
            open={isOpen}
            onCancel={onClose}
            footer={null}
            closeIcon={
                <Button
                    type="text"
                    icon={<CloseOutlined style={{ color: "white", fontSize: 18 }} />}
                    onClick={onClose}
                    style={{
                        marginTop: 17, marginRight: 47, padding: 6,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "1px solid white", color: "white",
                    }}
                />
            }
            destroyOnClose
            width={720}
            className="upm-modal-override"
            styles={{
                header: {
                    background: "#070C18",
                    borderRadius: "12px 12px 0 0",
                    padding: "16px 24px",
                    borderBottom: "none",
                },
                body: { padding: "20px 24px", background: "#f5f6fa", maxHeight: "70vh", overflowY: "auto" },
            }}
        >
            {FIELD_CONFIG.map(({ key, label }) => (
                <div key={key} className="upm-section">
                    <p className="upm-section-label">{label}</p>
                    <Upload
                        customRequest={makeCustomUpload(key)}
                        listType="picture-card"
                        accept="image/*"
                        multiple
                        fileList={fileLists[key]}
                        onChange={handleChange(key)}
                    >
                        {uploading[key] ? (
                            <div><LoadingOutlined /><div style={{ marginTop: 6 }}>Uploading…</div></div>
                        ) : (
                            <div><UploadOutlined /><div style={{ marginTop: 6 }}>Add</div></div>
                        )}
                    </Upload>
                </div>
            ))}

            <div className="upm-footer">
                <Button size="large" onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    size="large"
                    icon={<SaveOutlined />}
                    loading={submitting}
                    disabled={anyUploading}
                    onClick={handleSubmit}
                    style={{ background: "#0D152B", borderColor: "#0D152B" }}
                >
                    Save Changes
                </Button>
            </div>
        </Modal>
    );
};

export default UpdatePropertyMediaPopup;